import { useState } from "react";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Building2,
  CalendarDays,
  Download,
  ExternalLink,
  FileText,
  Handshake,
  Hash,
  Landmark,
  Hospital,
  MapPin,
  TreeDeciduous,
  TrendingUp,
  Users,
  type LucideIcon,
} from "lucide-react";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import {
  ImageReveal,
  Reveal,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { CsrPartnershipModal } from "@project/user/components/csr/csr-partnership-modal";
import { CsrSteps } from "@project/user/components/csr/csr-steps";
import { Button } from "@project/components/ui/button";
import { useI18n } from "@project/lib/use-i18n";

import bhagwanMahavirPreview from "@project/assets/csr/bhagwan-mahavir-foundation-trust-csr-certificate.png";
import manavSevaPreview from "@project/assets/csr/manav-seva-samaj-kalyan-trust-csr-certificate.png";

type CsrCertificate = {
  trust: string;
  registrationNo: string;
  pan: string;
  dated: string;
  srn: string;
  address: string;
  preview: string;
  pdf: string;
};

// Details as printed on the MCA approval letters in /public/docs/csr.
const CERTIFICATES: CsrCertificate[] = [
  {
    trust: "Bhagwan Mahavir Foundation Trust",
    registrationNo: "CSR00071971",
    pan: "AAETB4218P",
    dated: "26-04-2024",
    srn: "F94859832",
    address:
      "Flat No 102, Mahadev Landmark, Janhind Rasta, Maninagar, Ahmedabad, Gujarat – 380008",
    preview: bhagwanMahavirPreview,
    pdf: "/docs/csr/bhagwan-mahavir-foundation-trust-csr-certificate.pdf",
  },
  {
    trust: "Manav Seva Samaj Kalyan Trust",
    registrationNo: "CSR00015827",
    pan: "AAATM8600J",
    dated: "05-10-2021",
    srn: "T52982303",
    address:
      "Indira Nagar Vibhag 2, Padmavati Nagar, Lambha, Ahmedabad, Gujarat – 382405",
    preview: manavSevaPreview,
    pdf: "/docs/csr/manav-seva-samaj-kalyan-trust-csr-certificate.pdf",
  },
];

const HIGHLIGHTS: { icon: LucideIcon; n: 1 | 2 | 3 }[] = [
  { icon: Building2, n: 1 },
  { icon: TreeDeciduous, n: 2 },
  { icon: Hospital, n: 3 },
];

const BENEFITS: { icon: LucideIcon; n: 1 | 2 | 3 | 4 }[] = [
  { icon: BadgeCheck, n: 1 },
  { icon: TrendingUp, n: 2 },
  { icon: Users, n: 3 },
  { icon: Award, n: 4 },
];

function pdfFileName(trust: string) {
  return `${trust.replace(/\s+/g, "-")}-CSR-Certificate.pdf`;
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="bg-primary/10 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 break-words text-sm font-semibold text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

export default function CsrPartnershipPage() {
  const { t } = useI18n();
  const [modalOpen, setModalOpen] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);

  const openEnquiry = (preselect: string[] = []) => {
    setInterests(preselect);
    setModalOpen(true);
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={t("csrPage.hero.eyebrow")}
        title={t("csrPage.hero.title")}
        subtitle={t("csrPage.hero.subtitle")}
      >
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button
            size="lg"
            onClick={() => openEnquiry()}
            className="hover:bg-gold/90 group rounded-full bg-gold text-gold-foreground shadow-lg shadow-black/20"
          >
            <Handshake className="mr-2 h-4 w-4" />
            {t("home.csr.contactUs")}
            <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="rounded-full border-white/40 bg-white/10 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground"
          >
            <a href="#csr-certificates">
              <FileText className="mr-2 h-4 w-4" />
              {t("csrPage.certificates.heading")}
            </a>
          </Button>
        </div>
      </PageHero>

      {/* Why partner */}
      <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
        <Reveal variant="morph" className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("csrPage.intro.eyebrow")}
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            {t("csrPage.intro.heading")}{" "}
            <span className="italic text-gold">
              {t("csrPage.intro.headingHighlight")}
            </span>
          </h2>
        </Reveal>
        <Stagger className="mt-12 grid md:grid-cols-3" step={130}>
          {HIGHLIGHTS.map(({ icon: Icon, n }, i) => (
            <StaggerItem key={n} index={i} from="left" className="h-full">
              <div
                className={`group flex h-full flex-col border-l-2 border-primary px-6 py-6 transition-colors duration-300 hover:bg-card sm:px-9 sm:py-8 ${i === HIGHLIGHTS.length - 1 ? "md:border-r-2" : ""
                  }`}
              >
                <p className="text-[0.95rem] leading-relaxed text-foreground/80">
                  {t(`csrPage.intro.highlight${n}Text`)}
                </p>
                <Icon
                  className="mt-6 h-12 w-12 text-primary transition-transform duration-300 group-hover:-translate-y-1"
                  strokeWidth={1}
                />
                <h3 className="mt-4 !font-sans !text-xl !font-bold leading-snug text-primary sm:!text-2xl">
                  {t(`csrPage.intro.highlight${n}Title`)}
                </h3>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Benefits for the company */}
      <section className="relative overflow-hidden bg-primary px-4 py-14 text-primary-foreground sm:py-20">
        <div
          className="bg-gold/15 pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-white/10 blur-3xl"
          aria-hidden
        />
        <div className="relative mx-auto max-w-site">
          <Reveal variant="fade" className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-gold">
              {t("csrPage.benefits.eyebrow")}
            </p>
            <h2 className="mt-2 font-serif text-3xl font-semibold sm:text-4xl">
              {t("csrPage.benefits.heading")}
            </h2>
            <p className="text-primary-foreground/75 mx-auto mt-3 max-w-2xl text-sm sm:text-base">
              {t("csrPage.benefits.subtitle")}
            </p>
          </Reveal>
          <Stagger
            className="mt-12 mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
            step={120}
          >
            {BENEFITS.map(({ icon: Icon, n }, i) => (
              <StaggerItem key={n} index={i} className="h-full">
                <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-card p-6 text-foreground shadow-lg shadow-black/10 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl sm:p-7">
                  <span
                    className="pointer-events-none absolute right-5 top-3 select-none font-serif text-6xl font-bold text-[#9ca3af] transition-colors duration-300 group-hover:text-gold/30"
                    aria-hidden
                  >
                    {String(n).padStart(2, "0")}
                  </span>
                  <div className="grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary transition-all duration-300 group-hover:bg-gold group-hover:text-gold-foreground">
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </div>
                  <h3 className="mt-5 !font-sans !text-lg !font-bold leading-snug text-primary sm:!text-xl">
                    {t(`csrPage.benefits.item${n}Title`)}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {t(`csrPage.benefits.item${n}Text`)}
                  </p>
                  <span
                    className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100"
                    aria-hidden
                  />
                </article>
              </StaggerItem>
            ))}
          </Stagger>
          {/* <Reveal className="mt-10 text-center">
            <Button
              size="lg"
              onClick={() => openEnquiry()}
              className="hover:bg-gold/90 group rounded-full bg-gold text-gold-foreground shadow-lg shadow-black/20"
            >
              <Handshake className="mr-2 h-4 w-4" />
              {t("csrPage.benefits.cta")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Reveal> */}
        </div>
      </section>

      {/* Certificates */}
      <section
        id="csr-certificates"
        className="mx-auto max-w-site scroll-mt-24 px-4 py-14 sm:py-16"
      >
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
            {t("csrPage.certificates.eyebrow")}
          </p>
          <h2 className="mt-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
            {t("csrPage.certificates.heading")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t("csrPage.certificates.subtitle")}
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap items-start justify-center gap-8">
          {CERTIFICATES.map((cert, i) => (
            <Reveal
              key={cert.registrationNo}
              variant={i % 2 === 0 ? "left" : "right"}
              delay={i * 120}
            >
              <article className="flex w-fit max-w-full flex-col overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-sm transition-shadow hover:shadow-lg">
                <a
                  href={cert.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block border-b border-border bg-white"
                  aria-label={`${t("csrPage.certificates.view")} – ${cert.trust}`}
                >
                  {/* Fixed-size frame: both letters (different scan sizes) align and show from the top; the card hugs it. */}
                  <div className="h-[455px] w-[380px] max-w-full overflow-hidden p-4">
                    <ImageReveal
                      direction="up"
                      delay={150 + i * 120}
                      className="h-full w-full"
                    >
                      <img
                        src={cert.preview}
                        alt={`${t("csrPage.certificates.previewAlt")} – ${cert.trust}`}
                        loading="lazy"
                        className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                    </ImageReveal>
                  </div>
                  <span className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-0 py-0 text-[11px] font-bold text-primary-foreground shadow">
                    <BadgeCheck className="h-[19px] w-[19px]" />
                  </span>
                </a>

                <div className="flex flex-col px-4 py-4">
                  {/* <h3 className="font-serif text-2xl font-semibold text-foreground">
                    {cert.trust}
                  </h3>
                  <div className="border-gold/40 bg-gold/10 mt-3 inline-flex flex-wrap items-center gap-2 self-start rounded-xl border px-3 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("csrPage.certificates.registrationNo")}
                    </span>
                    <span className="font-mono text-base font-bold tracking-wide text-primary">
                      {cert.registrationNo}
                    </span>
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <DetailRow
                      icon={Hash}
                      label={t("csrPage.certificates.pan")}
                      value={cert.pan}
                    />
                    <DetailRow
                      icon={CalendarDays}
                      label={t("csrPage.certificates.dated")}
                      value={cert.dated}
                    />
                    <DetailRow
                      icon={FileText}
                      label={t("csrPage.certificates.srn")}
                      value={cert.srn}
                    />
                    <DetailRow
                      icon={Landmark}
                      label={t("csrPage.certificates.issuedBy")}
                      value="Registrar of Companies, ROC-Delhi"
                    />
                    <div className="sm:col-span-2">
                      <DetailRow
                        icon={MapPin}
                        label={t("csrPage.certificates.address")}
                        value={cert.address}
                      />
                    </div>
                  </div> */}

                  <div className="flex flex-wrap justify-center gap-3">
                    <Button asChild variant="outline" className="rounded-full">
                      <a
                        href={cert.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t("csrPage.certificates.view")}
                      </a>
                    </Button>
                    <Button asChild className="rounded-full">
                      <a href={cert.pdf} download={pdfFileName(cert.trust)}>
                        <Download className="mr-2 h-4 w-4" />
                        {t("csrPage.certificates.download")}
                      </a>
                    </Button>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-muted/40 px-4 py-14 sm:py-16">
        <div className="mx-auto max-w-site">
          <Reveal variant="morph" className="text-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
              {t("csr.process.eyebrow")}
            </p>
            <h2 className="mt-2 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
              {t("csr.process.title")}
            </h2>
          </Reveal>
          <CsrSteps />
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-14 sm:py-16">
        <Reveal variant="wipe-up">
          <div className="shadow-primary/25 relative mx-auto max-w-5xl overflow-hidden rounded-[2rem] bg-primary px-6 py-12 text-center text-primary-foreground shadow-2xl sm:px-12">
            <div
              className="bg-gold/25 pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl"
              aria-hidden
            />
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-gold to-transparent" />
            <Building2 className="relative mx-auto h-10 w-10 text-gold" />
            <h2 className="relative mt-4 font-serif text-3xl font-semibold sm:text-4xl">
              {t("csrPage.cta.heading")}
            </h2>
            <p className="text-primary-foreground/80 relative mx-auto mt-3 max-w-2xl text-sm sm:text-base">
              {t("csrPage.cta.text")}
            </p>
            <Button
              size="lg"
              onClick={() => openEnquiry()}
              className="hover:bg-gold/90 btn-lift group relative mt-8 rounded-full bg-gold text-gold-foreground shadow-lg shadow-black/20"
            >
              <Handshake className="mr-2 h-4 w-4" />
              {t("csrPage.cta.button")}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </Reveal>
      </section>

      <CsrPartnershipModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        defaultInterests={interests}
      />
    </PageShell>
  );
}
